// src/cockpit4/components/TransactionList.tsx
// ============================================
// TransactionList - 收支明细与自动分类（财务中心 P0-功能3）
//
// 功能：
//   1. 收支明细表格（日期/类型/对手方/金额/分类/备注/来源标签）
//   2. 多维度筛选（类型/分类/时间范围/金额范围）+ 关键词搜索
//   3. AI 自动分类引擎（基于关键词规则识别分类）
//   4. 异常交易自动标记（大额 >1万 / 频繁 同对手方>3次/月 / 新对手方）
//   5. 分页（每页 20 条）+ 排序（日期/金额）
//   6. 人工添加交易表单 + 分类可编辑修正
//   7. 导出 CSV
//
// 数据来源（白皮书铁律一：禁止假数据）：
//   - 收入明细：RuntimeManager.query('revenue_engine', { type: 'today_revenue' })
//     → 从 sourceBreakdown（CRM 成交 / SKU 销售 / 产品估算）拆分为收入明细，source='auto'
//   - 支出明细：localStorage(key: xeaos_finance_transactions) 持久化用户人工录入，source='manual'
//   - 无数据时显示空状态"暂无收支记录 / 点击添加"
//
// 权限：财务中心仅超级管理员开放（useUserStore.isSuperAdmin()）
// ============================================

import { useEffect, useState, useCallback, useMemo } from "react";
// Phase 2.3 财务 Vault 化：支出明细写透 Vault（跨设备）+ 挂载时从 Vault 回填
import { writeFinanceJson, hydrateFinanceKey } from "../../services/financeVaultStorage";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet,
  Plus,
  Search,
  Download,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  X,
  Database,
  Lock,
  Tag,
  Pencil,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { useUserStore } from "../../stores/userStore";

// ============================================
// 类型定义（与规格对齐）
// ============================================

type TransactionType = "income" | "expense";
type TransactionCategory =
  | "salary"
  | "rent"
  | "marketing"
  | "procurement"
  | "travel"
  | "tax"
  | "other";

interface Transaction {
  id: string;
  date: string; // YYYY-MM-DD
  type: TransactionType;
  counterparty: string; // 对手方
  amount: number;
  category: TransactionCategory;
  note?: string;
  source: "auto" | "manual"; // 自动统计 or 人工录入
  isAbnormal?: boolean; // 异常标记
  abnormalReason?: string; // 异常原因
  createdAt: string;
  /** Phase2-2.3：成交→记账→回款 关联（仅 crm_payment 自动记账） */
  deal_id?: string; // 关联 CRM 付款单号（记账凭证号）
  customer?: string; // 归因客户名
  settled?: boolean; // 是否已回款确认
}

// ============================================
// 常量：分类配置 / 自动分类规则 / localStorage key
// ============================================

const STORAGE_KEY = "xeaos_finance_transactions";
const PAGE_SIZE = 20;
const LARGE_AMOUNT_THRESHOLD = 10000; // 大额阈值
const FREQUENT_THRESHOLD = 3; // 同对手方本月频繁阈值

/** 分类标签与颜色配置 */
const CATEGORY_CONFIG: Record<
  TransactionCategory,
  { label: string; color: string }
> = {
  salary: { label: "工资", color: "#60A5FA" },
  rent: { label: "房租", color: "#A855F7" },
  marketing: { label: "营销", color: "#F59E0B" },
  procurement: { label: "采购", color: "#06B6D4" },
  travel: { label: "差旅", color: "#10B981" },
  tax: { label: "税费", color: "#F87171" },
  other: { label: "其他", color: "#9CA3AF" },
};

/** AI 自动分类规则引擎：关键词 → 分类 */
const CLASSIFY_RULES: Record<string, TransactionCategory> = {
  "工资|薪水|薪资": "salary",
  "房租|物业|水电": "rent",
  "广告|营销|推广|投放": "marketing",
  "采购|进货|原材料": "procurement",
  "差旅|交通|住宿|机票": "travel",
  "税|税费": "tax",
};

/** 收入来源标签映射（sourceBreakdown.source → 中文标签） */
const INCOME_SOURCE_LABEL: Record<string, string> = {
  crm_payment: "CRM 成交客户",
  sku_sales: "SKU 销售客户",
  product_runtime: "产品估算收入",
};

// ============================================
// 工具函数
// ============================================

/** 格式化金额（元 → 万元/元），防御 undefined/NaN */
function formatMoney(amount: number): string {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "¥ 0";
  if (Math.abs(num) >= 10000) {
    return `¥ ${(num / 10000).toFixed(2)} 万`;
  }
  return `¥ ${num.toLocaleString()}`;
}

/** 生成唯一 ID */
function genId(): string {
  return `tx-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** 获取今日日期 YYYY-MM-DD */
function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/** AI 自动分类：基于关键词规则匹配 counterparty + note */
function autoClassify(counterparty: string, note?: string): TransactionCategory {
  const text = `${counterparty || ""} ${note || ""}`;
  for (const [pattern, category] of Object.entries(CLASSIFY_RULES)) {
    const keywords = pattern.split("|");
    if (keywords.some((kw) => text.includes(kw))) {
      return category;
    }
  }
  return "other";
}

/**
 * 异常检测：对单条交易标记大额/频繁/新对手方
 * @param tx 当前交易
 * @param all 全部交易（用于统计同对手方频次与是否首次出现）
 */
function detectAbnormal(
  tx: Transaction,
  all: Transaction[]
): { isAbnormal: boolean; reason: string } {
  const reasons: string[] = [];

  // 1. 大额：金额 > 10000
  if (tx.amount > LARGE_AMOUNT_THRESHOLD) {
    reasons.push("大额");
  }

  // 2. 频繁：同对手方本月出现 > 3 次（含自身）
  const sameMonthSameParty = all.filter(
    (t) =>
      t.counterparty === tx.counterparty &&
      t.date.slice(0, 7) === tx.date.slice(0, 7)
  );
  if (sameMonthSameParty.length > FREQUENT_THRESHOLD) {
    reasons.push(`频繁(${sameMonthSameParty.length}次/月)`);
  }

  // 3. 新对手方：首次出现（按 createdAt 排序，最早出现的不算新）
  const sorted = [...all].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const firstSeen = sorted.find((t) => t.counterparty === tx.counterparty);
  if (firstSeen && firstSeen.id !== tx.id) {
    // 当前交易不是该对手方的首次出现 → 不是新对手方
  } else {
    reasons.push("新对手方");
  }

  return {
    isAbnormal: reasons.length > 0,
    reason: reasons.join("、"),
  };
}

/** 对全部交易批量计算异常标记 */
function recomputeAbnormal(all: Transaction[]): Transaction[] {
  return all.map((tx) => {
    const { isAbnormal, reason } = detectAbnormal(tx, all);
    return { ...tx, isAbnormal, abnormalReason: isAbnormal ? reason : undefined };
  });
}

// ============================================
// localStorage 持久化（支出明细：用户人工录入）
// ============================================

function loadManualTransactions(): Transaction[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as Transaction[];
  } catch {
    return [];
  }
}

function saveManualTransactions(list: Transaction[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
  // Phase 2.3：写透 Vault（跨设备真相源），失败不阻塞本地保存
  void writeFinanceJson("transactions.json", STORAGE_KEY, list);
}

// ============================================
// RevenueEngine 返回解包（双层包裹，参考 FinanceWorkspace）
// ============================================

interface SourceBreakdownItem {
  source: "crm_payment" | "sku_sales" | "product_runtime";
  amount: number;
  count: number;
  // Phase2-2.3：成交→记账→回款 关联
  customer?: string;
  dealId?: string;
  settled?: boolean;
}

interface TodayRevenueResp {
  date: string;
  todayAmount: number;
  todayCount: number;
  sourceBreakdown?: SourceBreakdownItem[];
  dataSource?: "real" | "partial" | "degraded";
  aggregatedAt?: string;
}

/** 解包 RuntimeManager.query 的双层返回，取出真实数据对象 */
function unwrapRevenue(resp: any): TodayRevenueResp | null {
  if (!resp?.success) return null;
  const inner = resp.data;
  if (inner && typeof inner === "object" && "data" in inner) {
    return ((inner as { data: unknown }).data as TodayRevenueResp) ?? null;
  }
  return (inner as TodayRevenueResp) ?? null;
}

/** 从 today_revenue.sourceBreakdown 合成收入明细（source='auto'） */
function buildIncomeTransactions(rev: TodayRevenueResp): Transaction[] {
  const date = rev.date || todayStr();
  const createdAt = rev.aggregatedAt || new Date().toISOString();
  const breakdown = rev.sourceBreakdown || [];
  if (breakdown.length === 0 && (rev.todayAmount || 0) > 0) {
    // 有总额但无分解 → 合成一条汇总收入
    return [
      {
        id: `auto-summary-${date}`,
        date,
        type: "income",
        counterparty: "收入汇总",
        amount: rev.todayAmount,
        category: "other",
        note: `今日成交 ${rev.todayCount || 0} 笔`,
        source: "auto",
        createdAt,
      },
    ];
  }
  return breakdown
    .filter((b) => b.amount > 0)
    .map((b) => {
      // Phase2-2.3：crm_payment 逐笔成交自带客户归因 → 记账到具体成交单（成交→记账→回款闭环）
      const isCrmDeal = b.source === "crm_payment" && !!b.customer;
      return {
        id: isCrmDeal ? `auto-${b.dealId || b.source}-${date}` : `auto-${b.source}-${date}`,
        date,
        type: "income" as TransactionType,
        counterparty: isCrmDeal ? b.customer! : INCOME_SOURCE_LABEL[b.source] || b.source,
        amount: b.amount,
        category: "other" as TransactionCategory,
        note: isCrmDeal
          ? `${b.count || 0} 笔成交回款${b.settled ? " · 已到账" : ""}`
          : `${b.count || 0} 笔成交`,
        source: "auto" as const,
        createdAt,
        deal_id: b.dealId,
        customer: isCrmDeal ? b.customer : undefined,
        settled: isCrmDeal ? b.settled : undefined,
      };
    });
}

// ============================================
// 主组件
// ============================================

export default function TransactionList() {
  // 权限：财务中心仅超级管理员开放
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin());

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  // 筛选 / 搜索 / 排序 状态
  const [keyword, setKeyword] = useState("");
  const [filterType, setFilterType] = useState<"all" | TransactionType>("all");
  const [filterCategory, setFilterCategory] = useState<"all" | TransactionCategory>(
    "all"
  );
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterAmountMin, setFilterAmountMin] = useState("");
  const [filterAmountMax, setFilterAmountMax] = useState("");
  const [sortField, setSortField] = useState<"date" | "amount">("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  // 添加表单 / 分类编辑 状态
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);

  // 添加表单字段
  const [form, setForm] = useState({
    date: todayStr(),
    type: "expense" as TransactionType,
    counterparty: "",
    amount: "",
    category: "other" as TransactionCategory,
    note: "",
    userTouchedCategory: false,
  });

  // ============================================
  // 数据加载：RevenueEngine（收入） + localStorage（支出）
  // ============================================
  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // 轮询等待 RuntimeManager 初始化
      let ready = false;
      for (let i = 0; i < 40; i++) {
        if (RuntimeManager.isInitialized()) {
          ready = true;
          break;
        }
        await new Promise((r) => setTimeout(r, 300));
      }
      let incomeList: Transaction[] = [];
      if (ready && RuntimeManager.hasRuntime("revenue_engine" as any)) {
        const resp = await RuntimeManager.query("revenue_engine" as any, {
          type: "today_revenue",
        });
        const rev = unwrapRevenue(resp);
        if (rev) incomeList = buildIncomeTransactions(rev);
      }
      // 支出明细：localStorage 持久化
      const manualList = loadManualTransactions();
      // 合并 + 重算异常标记
      const merged = recomputeAbnormal([...incomeList, ...manualList]);
      setTransactions(merged);
    } catch (err: any) {
      setError(err?.message || "加载收支明细失败");
      // 加载失败时仍展示 localStorage 中的支出
      const manualList = loadManualTransactions();
      setTransactions(recomputeAbnormal(manualList));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Phase 2.3：挂载时从 Vault 同步财务数据到 localStorage（跨设备真相源回填）
  useEffect(() => {
    async function hydrateFinanceData() {
      const hydrated = await hydrateFinanceKey<Transaction[]>(
        "transactions.json",
        STORAGE_KEY
      );
      if (hydrated) {
        // Vault 数据优先，覆盖本地（含收入 auto 明细需保留，故先取本地收入再合并）
        const incomeList = transactions.filter((t) => t.source === "auto");
        setTransactions(recomputeAbnormal([...incomeList, ...hydrated]));
      }
    }
    hydrateFinanceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // 筛选 + 排序 + 分页（useMemo 避免重算）
  // ============================================
  const filtered = useMemo(() => {
    let list = transactions.slice();
    // 类型筛选
    if (filterType !== "all") {
      list = list.filter((t) => t.type === filterType);
    }
    // 分类筛选
    if (filterCategory !== "all") {
      list = list.filter((t) => t.category === filterCategory);
    }
    // 时间范围筛选
    if (filterDateFrom) {
      list = list.filter((t) => t.date >= filterDateFrom);
    }
    if (filterDateTo) {
      list = list.filter((t) => t.date <= filterDateTo);
    }
    // 金额范围筛选
    if (filterAmountMin) {
      const min = Number(filterAmountMin);
      if (Number.isFinite(min)) list = list.filter((t) => t.amount >= min);
    }
    if (filterAmountMax) {
      const max = Number(filterAmountMax);
      if (Number.isFinite(max)) list = list.filter((t) => t.amount <= max);
    }
    // 关键词搜索（对手方 / 备注）
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      list = list.filter(
        (t) =>
          t.counterparty.toLowerCase().includes(kw) ||
          (t.note || "").toLowerCase().includes(kw)
      );
    }
    // 排序
    list.sort((a, b) => {
      let cmp = 0;
      if (sortField === "date") {
        cmp = a.date.localeCompare(b.date) || a.createdAt.localeCompare(b.createdAt);
      } else {
        cmp = a.amount - b.amount;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [
    transactions,
    filterType,
    filterCategory,
    filterDateFrom,
    filterDateTo,
    filterAmountMin,
    filterAmountMax,
    keyword,
    sortField,
    sortDir,
  ]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // 统计：收入/支出总额
  const totalIncome = transactions
    .filter((t) => t.type === "income")
    .reduce((s, t) => s + t.amount, 0);
  const totalExpense = transactions
    .filter((t) => t.type === "expense")
    .reduce((s, t) => s + t.amount, 0);
  const abnormalCount = transactions.filter((t) => t.isAbnormal).length;

  // ============================================
  // 操作：添加交易 / 修改分类 / 导出 CSV
  // ============================================

  /** 重置表单 */
  const resetForm = () => {
    setForm({
      date: todayStr(),
      type: "expense",
      counterparty: "",
      amount: "",
      category: "other",
      note: "",
      userTouchedCategory: false,
    });
  };

  /** 提交添加交易表单 */
  const handleAddTransaction = () => {
    const counterparty = form.counterparty.trim();
    const amountNum = Number(form.amount);
    if (!counterparty) {
      setError("对手方不能为空");
      return;
    }
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setError("金额必须为正数");
      return;
    }
    // AI 自动分类（用户未手动改分类时）
    const category = form.userTouchedCategory
      ? form.category
      : autoClassify(counterparty, form.note);

    const newTx: Transaction = {
      id: genId(),
      date: form.date || todayStr(),
      type: form.type,
      counterparty,
      amount: amountNum,
      category,
      note: form.note.trim() || undefined,
      source: "manual",
      createdAt: new Date().toISOString(),
    };

    const manualList = loadManualTransactions();
    manualList.push(newTx);
    saveManualTransactions(manualList);

    // 合并 + 重算异常
    setTransactions((prev) => recomputeAbnormal([...prev, newTx]));
    setError("");
    setShowAddForm(false);
    resetForm();
  };

  /** 修改分类（点击分类标签触发，仅 manual 持久化，auto 仅会话内） */
  const handleCategoryChange = (txId: string, newCategory: TransactionCategory) => {
    setTransactions((prev) => {
      const updated = prev.map((t) =>
        t.id === txId ? { ...t, category: newCategory } : t
      );
      // 持久化 manual 交易
      const manualOnly = updated
        .filter((t) => t.source === "manual")
        .map(({ isAbnormal, abnormalReason, ...rest }) => rest as Transaction);
      saveManualTransactions(manualOnly);
      return updated;
    });
    setEditingCategoryId(null);
  };

  /** 导出 CSV（含 BOM 头，Excel 中文兼容） */
  const handleExportCSV = () => {
    const headers = [
      "ID",
      "日期",
      "类型",
      "对手方",
      "金额",
      "分类",
      "备注",
      "来源",
      "异常",
      "异常原因",
    ];
    const rows = filtered.map((t) => [
      t.id,
      t.date,
      t.type === "income" ? "收入" : "支出",
      t.counterparty,
      String(t.amount),
      CATEGORY_CONFIG[t.category].label,
      t.note || "",
      t.source === "auto" ? "自动统计" : "人工录入",
      t.isAbnormal ? "是" : "否",
      t.abnormalReason || "",
    ]);
    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            const s = String(cell ?? "");
            return s.includes(",") || s.includes('"') || s.includes("\n")
              ? `"${s.replace(/"/g, '""')}"`
              : s;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `xeaos-transactions-${todayStr()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** 表单输入时自动分类（用户未手动改分类时） */
  const handleFormInput = (field: keyof typeof form, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value } as typeof prev;
      if (!prev.userTouchedCategory && (field === "counterparty" || field === "note")) {
        next.category = autoClassify(next.counterparty, next.note);
      }
      return next;
    });
  };

  /** 切换排序字段/方向 */
  const toggleSort = (field: "date" | "amount") => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  /** 重置筛选 */
  const resetFilters = () => {
    setKeyword("");
    setFilterType("all");
    setFilterCategory("all");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterAmountMin("");
    setFilterAmountMax("");
    setPage(1);
  };

  // ============================================
  // 渲染：非超管权限提示
  // ============================================
  if (!isSuperAdmin) {
    return (
      <div className="h-full w-full overflow-hidden p-4">
        <div className="h-full flex flex-col items-center justify-center rounded-xl border border-red-500/20 bg-red-500/[0.02] backdrop-blur-xl">
          <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
            <Lock size={28} className="text-red-400" />
          </div>
          <h2 className="text-[16px] font-semibold text-white mb-2">权限受限</h2>
          <p className="text-[12px] font-mono text-gray-400 text-center max-w-sm leading-relaxed">
            财务中心仅对超级管理员开放。
            <br />
            普通员工无权查看收支明细。
          </p>
          <p className="text-[10px] font-mono text-gray-600 mt-4">
            如需访问，请联系超级管理员授权。
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // 渲染：主面板
  // ============================================
  return (
    <div className="h-full w-full overflow-hidden p-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Wallet size={14} className="text-emerald-400" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white tracking-tight">
                收支明细与自动分类
              </h2>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                Transaction List · 收入/支出 · AI 分类 · 异常检测
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCSV}
              disabled={filtered.length === 0}
              className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-white/[0.08] bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] transition disabled:opacity-40"
            >
              <Download size={10} />
              导出 CSV
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowAddForm(true);
              }}
              className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition"
            >
              <Plus size={10} />
              添加交易
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-white/[0.08] bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] transition disabled:opacity-50"
            >
              <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
              {loading ? "加载中" : "刷新"}
            </button>
          </div>
        </div>

        {/* 统计概览 */}
        <div className="grid grid-cols-3 gap-3 px-5 py-3 border-b border-white/[0.06]">
          <div className="rounded-lg border border-emerald-500/15 bg-emerald-500/[0.03] px-3 py-2">
            <div className="text-[9px] font-mono text-emerald-400/70 uppercase">收入总额</div>
            <div className="text-[14px] font-mono font-bold text-emerald-400">
              {formatMoney(totalIncome)}
            </div>
          </div>
          <div className="rounded-lg border border-red-500/15 bg-red-500/[0.03] px-3 py-2">
            <div className="text-[9px] font-mono text-red-400/70 uppercase">支出总额</div>
            <div className="text-[14px] font-mono font-bold text-red-400">
              {formatMoney(totalExpense)}
            </div>
          </div>
          <div className="rounded-lg border border-amber-500/15 bg-amber-500/[0.03] px-3 py-2">
            <div className="text-[9px] font-mono text-amber-400/70 uppercase">异常交易</div>
            <div className="text-[14px] font-mono font-bold text-amber-400">
              {abnormalCount} 笔
            </div>
          </div>
        </div>

        {/* 筛选 / 搜索 工具栏 */}
        <div className="px-5 py-3 border-b border-white/[0.06] space-y-2">
          {/* 第一行：搜索 + 类型 + 分类 */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500"
              />
              <input
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setPage(1);
                }}
                placeholder="搜索对手方 / 备注"
                className="w-full pl-7 pr-2 py-1.5 text-[11px] font-mono rounded border border-white/[0.08] bg-white/[0.02] text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value as typeof filterType);
                setPage(1);
              }}
              className="text-[11px] font-mono px-2 py-1.5 rounded border border-white/[0.08] bg-white/[0.02] text-gray-300 focus:outline-none focus:border-emerald-500/40"
            >
              <option value="all">全部类型</option>
              <option value="income">收入</option>
              <option value="expense">支出</option>
            </select>
            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value as typeof filterCategory);
                setPage(1);
              }}
              className="text-[11px] font-mono px-2 py-1.5 rounded border border-white/[0.08] bg-white/[0.02] text-gray-300 focus:outline-none focus:border-emerald-500/40"
            >
              <option value="all">全部分类</option>
              {(Object.keys(CATEGORY_CONFIG) as TransactionCategory[]).map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_CONFIG[c].label}
                </option>
              ))}
            </select>
            <button
              onClick={resetFilters}
              className="text-[10px] font-mono px-2 py-1.5 rounded border border-white/[0.08] bg-white/[0.02] text-gray-400 hover:text-gray-200 hover:bg-white/[0.06] transition"
            >
              重置
            </button>
          </div>
          {/* 第二行：时间范围 + 金额范围 */}
          <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono text-gray-500">
            <span>时间</span>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => {
                setFilterDateFrom(e.target.value);
                setPage(1);
              }}
              className="px-2 py-1 rounded border border-white/[0.08] bg-white/[0.02] text-gray-300 focus:outline-none focus:border-emerald-500/40"
            />
            <span>至</span>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => {
                setFilterDateTo(e.target.value);
                setPage(1);
              }}
              className="px-2 py-1 rounded border border-white/[0.08] bg-white/[0.02] text-gray-300 focus:outline-none focus:border-emerald-500/40"
            />
            <span className="ml-2">金额</span>
            <input
              type="number"
              value={filterAmountMin}
              onChange={(e) => {
                setFilterAmountMin(e.target.value);
                setPage(1);
              }}
              placeholder="最小"
              className="w-16 px-2 py-1 rounded border border-white/[0.08] bg-white/[0.02] text-gray-300 placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/40"
            />
            <span>至</span>
            <input
              type="number"
              value={filterAmountMax}
              onChange={(e) => {
                setFilterAmountMax(e.target.value);
                setPage(1);
              }}
              placeholder="最大"
              className="w-16 px-2 py-1 rounded border border-white/[0.08] bg-white/[0.02] text-gray-300 placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/40"
            />
          </div>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mx-5 mt-3 px-3 py-2 rounded-md border border-red-500/20 bg-red-500/[0.03] text-[10px] font-mono text-red-400">
            <AlertTriangle size={11} className="inline mr-1" />
            {error}
          </div>
        )}

        {/* 内容区：表格 / 空状态 */}
        <div className="flex-1 overflow-auto">
          {pageItems.length === 0 ? (
            /* 空状态：无数据提示 */
            <div className="h-full flex flex-col items-center justify-center py-12">
              <div className="w-14 h-14 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center mb-3">
                <Database size={22} className="text-gray-600" />
              </div>
              <p className="text-[12px] font-mono text-gray-500">暂无收支记录</p>
              <button
                onClick={() => {
                  resetForm();
                  setShowAddForm(true);
                }}
                className="mt-3 text-[10px] font-mono px-3 py-1.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition"
              >
                <Plus size={10} className="inline mr-1" />
                点击添加
              </button>
            </div>
          ) : (
            <table className="w-full text-[11px] font-mono">
              {/* 表头 */}
              <thead className="sticky top-0 bg-white/[0.02] backdrop-blur-xl border-b border-white/[0.06]">
                <tr className="text-left text-gray-500">
                  <th
                    className="px-4 py-2 cursor-pointer select-none hover:text-gray-300"
                    onClick={() => toggleSort("date")}
                  >
                    <span className="inline-flex items-center gap-1">
                      日期
                      <ArrowUpDown size={9} className={sortField === "date" ? "text-emerald-400" : ""} />
                    </span>
                  </th>
                  <th className="px-4 py-2">类型</th>
                  <th className="px-4 py-2">对手方</th>
                  <th
                    className="px-4 py-2 cursor-pointer select-none hover:text-gray-300 text-right"
                    onClick={() => toggleSort("amount")}
                  >
                    <span className="inline-flex items-center gap-1">
                      金额
                      <ArrowUpDown size={9} className={sortField === "amount" ? "text-emerald-400" : ""} />
                    </span>
                  </th>
                  <th className="px-4 py-2">分类</th>
                  <th className="px-4 py-2">备注</th>
                  <th className="px-4 py-2">来源</th>
                </tr>
              </thead>
              {/* 表体 */}
              <tbody>
                {pageItems.map((tx, idx) => {
                  const catCfg = CATEGORY_CONFIG[tx.category];
                  return (
                    <motion.tr
                      key={tx.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2, delay: idx * 0.02 }}
                      className="border-b border-white/[0.04] hover:bg-white/[0.02] transition"
                    >
                      {/* 日期 */}
                      <td className="px-4 py-2 text-gray-300">{tx.date}</td>
                      {/* 类型 */}
                      <td className="px-4 py-2">
                        {tx.type === "income" ? (
                          <span className="inline-flex items-center gap-0.5 text-emerald-400">
                            <ArrowUpRight size={11} />
                            收入
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 text-red-400">
                            <ArrowDownRight size={11} />
                            支出
                          </span>
                        )}
                      </td>
                      {/* 对手方（异常标记 + Phase2-2.3 成交回款归因） */}
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-200">{tx.counterparty}</span>
                          {/* 成交→记账→回款 关联标签 */}
                          {tx.customer && (
                            <span
                              title={`成交单 ${tx.deal_id || "—"}${tx.settled ? "" : " · 待到账"}`}
                              className={`inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded ${
                                tx.settled
                                  ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                                  : "bg-amber-500/15 text-amber-400 border border-amber-500/30"
                              }`}
                            >
                              {tx.settled ? (
                                <ArrowUpRight size={9} />
                              ) : (
                                <AlertTriangle size={9} />
                              )}
                              {tx.settled ? "已回款" : "待到账"}
                            </span>
                          )}
                          {tx.isAbnormal && (
                            <span
                              title={tx.abnormalReason}
                              className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30"
                            >
                              <AlertTriangle size={9} />
                              {tx.abnormalReason}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* 金额 */}
                      <td
                        className={`px-4 py-2 text-right font-bold ${
                          tx.type === "income" ? "text-emerald-400" : "text-red-400"
                        }`}
                      >
                        {tx.type === "income" ? "+" : "-"}
                        {formatMoney(tx.amount)}
                      </td>
                      {/* 分类（可编辑） */}
                      <td className="px-4 py-2">
                        {editingCategoryId === tx.id ? (
                          <select
                            autoFocus
                            value={tx.category}
                            onChange={(e) =>
                              handleCategoryChange(
                                tx.id,
                                e.target.value as TransactionCategory
                              )
                            }
                            onBlur={() => setEditingCategoryId(null)}
                            className="text-[10px] px-1 py-0.5 rounded border border-white/[0.08] bg-white/[0.04] text-gray-200 focus:outline-none focus:border-emerald-500/40"
                          >
                            {(Object.keys(CATEGORY_CONFIG) as TransactionCategory[]).map(
                              (c) => (
                                <option key={c} value={c}>
                                  {CATEGORY_CONFIG[c].label}
                                </option>
                              )
                            )}
                          </select>
                        ) : (
                          <button
                            onClick={() => setEditingCategoryId(tx.id)}
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border hover:opacity-80 transition"
                            style={{
                              color: catCfg.color,
                              borderColor: `${catCfg.color}40`,
                              background: `${catCfg.color}15`,
                            }}
                            title="点击修改分类"
                          >
                            <Tag size={9} />
                            {catCfg.label}
                            <Pencil size={8} className="opacity-50" />
                          </button>
                        )}
                      </td>
                      {/* 备注 */}
                      <td className="px-4 py-2 text-gray-400 max-w-[160px] truncate">
                        {tx.note || "—"}
                      </td>
                      {/* 来源标签 */}
                      <td className="px-4 py-2">
                        {tx.source === "auto" ? (
                          <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
                            <Database size={9} />
                            自动统计
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">
                            人工录入
                          </span>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* 分页 */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] text-[10px] font-mono text-gray-500">
            <span>
              共 {filtered.length} 条 · 第 {currentPage}/{totalPages} 页
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="p-1 rounded border border-white/[0.08] bg-white/[0.02] text-gray-400 hover:bg-white/[0.06] transition disabled:opacity-30"
              >
                <ChevronLeft size={12} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="p-1 rounded border border-white/[0.08] bg-white/[0.02] text-gray-400 hover:bg-white/[0.06] transition disabled:opacity-30"
              >
                <ChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* ============================================ */}
      {/* 添加交易表单（模态） */}
      {/* ============================================ */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setShowAddForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="w-[420px] rounded-xl border border-white/[0.08] bg-[#0a0e14]/95 backdrop-blur-xl p-5 shadow-2xl"
            >
              {/* 表单头 */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                    <Plus size={12} className="text-emerald-400" />
                  </div>
                  <h3 className="text-[13px] font-semibold text-white">添加交易</h3>
                </div>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-white/[0.06] transition"
                >
                  <X size={14} />
                </button>
              </div>

              {/* 表单字段 */}
              <div className="space-y-3">
                {/* 类型切换 */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setForm((f) => ({ ...f, type: "expense" }))}
                    className={`flex-1 py-1.5 text-[11px] font-mono rounded border transition ${
                      form.type === "expense"
                        ? "border-red-500/40 bg-red-500/10 text-red-400"
                        : "border-white/[0.08] bg-white/[0.02] text-gray-500"
                    }`}
                  >
                    支出
                  </button>
                  <button
                    onClick={() => setForm((f) => ({ ...f, type: "income" }))}
                    className={`flex-1 py-1.5 text-[11px] font-mono rounded border transition ${
                      form.type === "income"
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                        : "border-white/[0.08] bg-white/[0.02] text-gray-500"
                    }`}
                  >
                    收入
                  </button>
                </div>

                {/* 日期 */}
                <div>
                  <label className="text-[10px] font-mono text-gray-500 block mb-1">日期</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    className="w-full px-2 py-1.5 text-[11px] font-mono rounded border border-white/[0.08] bg-white/[0.02] text-gray-200 focus:outline-none focus:border-emerald-500/40"
                  />
                </div>

                {/* 对手方 */}
                <div>
                  <label className="text-[10px] font-mono text-gray-500 block mb-1">对手方</label>
                  <input
                    value={form.counterparty}
                    onChange={(e) => handleFormInput("counterparty", e.target.value)}
                    placeholder="如：工资发放、广告投放、采购供应商"
                    className="w-full px-2 py-1.5 text-[11px] font-mono rounded border border-white/[0.08] bg-white/[0.02] text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/40"
                  />
                </div>

                {/* 金额 */}
                <div>
                  <label className="text-[10px] font-mono text-gray-500 block mb-1">金额（元）</label>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={(e) => handleFormInput("amount", e.target.value)}
                    placeholder="请输入金额"
                    className="w-full px-2 py-1.5 text-[11px] font-mono rounded border border-white/[0.08] bg-white/[0.02] text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/40"
                  />
                </div>

                {/* 分类（AI 自动识别 + 手动修正） */}
                <div>
                  <label className="text-[10px] font-mono text-gray-500 block mb-1">
                    分类
                    <span className="ml-1 text-emerald-400/70">（AI 自动识别，可修改）</span>
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => {
                      setForm((f) => ({
                        ...f,
                        category: e.target.value as TransactionCategory,
                        userTouchedCategory: true,
                      }));
                    }}
                    className="w-full px-2 py-1.5 text-[11px] font-mono rounded border border-white/[0.08] bg-white/[0.02] text-gray-200 focus:outline-none focus:border-emerald-500/40"
                  >
                    {(Object.keys(CATEGORY_CONFIG) as TransactionCategory[]).map((c) => (
                      <option key={c} value={c}>
                        {CATEGORY_CONFIG[c].label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 备注 */}
                <div>
                  <label className="text-[10px] font-mono text-gray-500 block mb-1">备注</label>
                  <input
                    value={form.note}
                    onChange={(e) => handleFormInput("note", e.target.value)}
                    placeholder="可选，输入关键词可提升 AI 分类准确度"
                    className="w-full px-2 py-1.5 text-[11px] font-mono rounded border border-white/[0.08] bg-white/[0.02] text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/40"
                  />
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => setShowAddForm(false)}
                  className="text-[11px] font-mono px-3 py-1.5 rounded border border-white/[0.08] bg-white/[0.02] text-gray-400 hover:bg-white/[0.06] transition"
                >
                  取消
                </button>
                <button
                  onClick={handleAddTransaction}
                  className="text-[11px] font-mono px-3 py-1.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition"
                >
                  确认添加
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
